/**
 * OPEN-217 (DEC-382) — on the billing ticket, the Warnings the driver saw on the phone and
 * confirmed ("You entered 180 bbl. Are you sure?" → "Yes, it's right"), beside the driver's
 * notes. Served beside the ticket read model, so it never reaches the invoice backup page.
 * Renders nothing when there are none.
 */

import * as React from 'react';
import { Info } from 'lucide-react';
import {
  VALIDATION_RULE_FIELDS,
  type ValidationRuleField,
} from '@lodex-pro/shared/validation-rules';
import { formatTimestamp } from '../../lib/timestamp';
import type { DriverConfirmedWarning } from './hooks';

/** The field's label, or the raw code for a field this build doesn't know. */
function fieldLabel(field: string): string {
  return field in VALIDATION_RULE_FIELDS
    ? VALIDATION_RULE_FIELDS[field as ValidationRuleField].label
    : field;
}

export function DriverConfirmedWarnings({
  warnings,
  tz,
}: {
  warnings: readonly DriverConfirmedWarning[] | undefined;
  tz: string;
}): React.JSX.Element | null {
  if (warnings === undefined || warnings.length === 0) return null;
  return (
    <div style={{ marginTop: '12px' }} data-testid="driver-confirmed-warnings">
      {warnings.map((w, i) => (
        <p
          key={`${w.ruleId}-${String(i)}`}
          style={{ margin: '0 0 4px', fontSize: '14px', display: 'flex', gap: '6px' }}
        >
          <Info size={16} aria-hidden style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>
            Driver confirmed: “{w.message}”{' '}
            <span style={{ color: 'var(--color-text-tertiary)' }}>
              ({fieldLabel(w.field)}
              {w.position !== null ? `, tank ${String(w.position + 1)}` : ''},{' '}
              {formatTimestamp(w.confirmedAt, tz)})
            </span>
          </span>
        </p>
      ))}
    </div>
  );
}
